import React, { useState, useEffect } from "react";

import ReusableTable from "components/Tables/ReusableTable";

import {
  Flex,
  Box,
  Text,
} from "@chakra-ui/react";

import {
  EllipsisVertical,
  Pencil,
  EyeIcon,
  MessageSquare,
  Gift,
  FileText,
} from "lucide-react";

import ViewDriver from "./components/ViewDriver";
import EditDriverDrawer from "./components/EditDriverDrawer";

import ReusableDrawer from "../../../components/Drawers/ReusableDrawer.js";

import ThreeDotMenu from "components/Menus/ThreeDotMenu";

import GradientButton from "components/Buttons/GradientButton";

import { useDispatch, useSelector } from "react-redux";

import {
  fetchDriversAction,
} from "../../../state/actions/driverActions";

import {
  formatDate,
} from "utils/dateFormatter";

import {
  useCustomNotification,
} from "components/Toasts/OpenNotification.js";

import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";

import { useHistory } from "react-router-dom";

import {
  adminTopUpWalletAction,
  fetchDriverWalletsAction,
} from "state/actions/driverWalletActions";

import AdminTopUpWalletDrawer from "./components/AdminTopUpWalletDrawer";

import StatusBadge, {
  getStatusVariant,
} from "components/Badges/StatusBadge";

import AddContactLogDrawer from "../ContactLogManagement/components/AddContactLogDrawer";

import ViewContactLogsDrawer from "../ContactLogManagement/components/ViewContactLogsDrawer";

import {
  createContactLogAction,
  fetchUserContactLogsAction,
} from "../../../state/actions/contactLogActions";


const DriverManagement = () => {

  /* ===================================================== */
  /* DRAWERS */
  /* ===================================================== */

  const [
    isViewDriverDrawerOpen,
    setViewDriverDrawerOpen,
  ] = useState(false);

  const [
    isEditDriverDrawerOpen,
    setEditDriverDrawerOpen,
  ] = useState(false);

  const [
    isAllocateDrawerOpen,
    setAllocateDrawerOpen,
  ] = useState(false);

  const [
    isAddContactLogOpen,
    setAddContactLogOpen,
  ] = useState(false);

  const [
    isContactLogsOpen,
    setContactLogsOpen,
  ] = useState(false);


  /* ===================================================== */
  /* SELECTED DATA */
  /* ===================================================== */

  const [
    selectedDriver,
    setSelectedDriver,
  ] = useState(null);

  const [
    selectedUserForLog,
    setSelectedUserForLog,
  ] = useState(null);

  const [
    contactLogsUser,
    setContactLogsUser,
  ] = useState(null);


  /* ===================================================== */
  /* CONTACT STATUS */
  /* ===================================================== */

  const [
    contactStatuses,
    setContactStatuses,
  ] = useState({});


  /* ===================================================== */
  /* PAGINATION */
  /* ===================================================== */

  const [
    currentPage,
    setCurrentPage,
  ] = useState(1);

  const PAGE_SIZE = 20;


  /* ===================================================== */
  /* SEARCH */
  /* ===================================================== */

  const [
    searchField,
    setSearchField,
  ] = useState(null);

  const [
    searchValue,
    setSearchValue,
  ] = useState("");

  const [
    searchTerm,
    setSearchTerm,
  ] = useState("");


  const dispatch = useDispatch();

  const showToast =
    useCustomNotification();

  const history =
    useHistory();


  /* ===================================================== */
  /* REDUX */
  /* ===================================================== */

  const {
    drivers,
    wallets,
    loading,
    error,
    hasMore,
    total,
  } = useSelector(
    (state) => state.drivers
  );


  const {
    logs: contactLogs,
    loading:
      contactLogsLoading,
  } = useSelector(
    (state) => state.contactLogs
  );


  /* ===================================================== */
  /* SEARCH HANDLER */
  /* ===================================================== */

  const handleSearch = (
    field,
    value
  ) => {

    setCurrentPage(1);

    setSearchField(field);

    setSearchTerm(value);
  };


  useEffect(() => {

    const timer =
      setTimeout(() => {

        setSearchValue(
          searchTerm
        );

      }, 500);

    return () =>
      clearTimeout(timer);

  }, [
    searchTerm,
  ]);


  /* ===================================================== */
  /* FETCH DRIVERS */
  /* ===================================================== */

  useEffect(() => {

    dispatch(
      fetchDriversAction(
        currentPage,
        PAGE_SIZE,
        searchField,
        searchValue
      )
    );

    dispatch(
      fetchDriverWalletsAction()
    );

  }, [
    dispatch,
    currentPage,
    searchField,
    searchValue,
  ]);


  /* ===================================================== */
  /* LOAD CONTACT STATUS */
  /* ===================================================== */

  useEffect(() => {

    const loadContactStatuses =
      async () => {

        const driverList =
          Array.isArray(
            drivers?.data
          )
            ? drivers.data
            : [];


        if (
          !driverList.length
        ) {

          setContactStatuses(
            {}
          );

          return;
        }


        const statuses = {};


        await Promise.all(
          driverList.map(
            async (
              driver
            ) => {

              const userId =
                driver
                  ?.user
                  ?.user_id;


              if (!userId) {
                return;
              }


              const response =
                await dispatch(
                  fetchUserContactLogsAction(
                    userId
                  )
                );


              if (
                response?.success &&
                Array.isArray(
                  response.data
                ) &&
                response.data.length
              ) {

                const latestLog =
                  [
                    ...response.data,
                  ].sort(
                    (
                      a,
                      b
                    ) =>
                      new Date(
                        b.contacted_at
                      ) -
                      new Date(
                        a.contacted_at
                      )
                  )[0];


                statuses[userId] =
                  latestLog?.status ||
                  "Not Contacted";

              } else {

                statuses[userId] =
                  "Not Contacted";
              }

            }
          )
        );


        setContactStatuses(
          statuses
        );
      };


    loadContactStatuses();

  }, [
    drivers?.data,
    dispatch,
  ]);


  /* ===================================================== */
  /* VIEW DRIVER */
  /* ===================================================== */

  const handleViewDriver = (
    driver
  ) => {

    const wallet =
      walletLookup[
        driver.driver?.driver_id
      ];


    setSelectedDriver({
      ...driver,
      wallet,
    });


    setViewDriverDrawerOpen(
      true
    );
  };


  const handleCloseViewDriverDrawer =
    () => {

      setSelectedDriver(
        null
      );

      setViewDriverDrawerOpen(
        false
      );
    };


  /* ===================================================== */
  /* EDIT DRIVER */
  /* ===================================================== */

  const handleOpenEditDriverDrawer =
    (driver) => {

      setSelectedDriver(
        driver
      );

      setEditDriverDrawerOpen(
        true
      );
    };


  const handleCloseEditDriverDrawer =
    () => {

      setSelectedDriver(
        null
      );

      setEditDriverDrawerOpen(
        false
      );
    };


  /* ===================================================== */
  /* TOP UP */
  /* ===================================================== */

  const handleOpenAllocateDrawer =
    (driver) => {

      setSelectedDriver(
        driver
      );

      setAllocateDrawerOpen(
        true
      );
    };


  const handleCloseAllocateDrawer =
    () => {

      setSelectedDriver(
        null
      );

      setAllocateDrawerOpen(
        false
      );
    };


  const handleTopUpWallet =
    async ({
      driver_id,
      amount,
    }) => {

      const response =
        await dispatch(
          adminTopUpWalletAction(
            driver_id,
            amount
          )
        );


      if (
        response.success
      ) {

        showToast({
          type: "success",
          message:
            "Wallet Topped Up",
          description:
            "Complimentary wallet top-up allocated successfully.",
        });


        handleCloseAllocateDrawer();

      } else {

        showToast({
          type: "error",
          message:
            "Top-up Failed",
          description:
            response.error,
        });
      }
    };


  /* ===================================================== */
  /* ADD CONTACT LOG */
  /* ===================================================== */

  const handleOpenAddContactLog =
    (driver) => {

      setSelectedUserForLog(
        driver?.user || null
      );

      setAddContactLogOpen(
        true
      );
    };


  const handleAddContactLog =
    async (data) => {

      const response =
        await dispatch(
          createContactLogAction(
            data
          )
        );


      if (
        response?.success
      ) {

        showToast({
          type: "success",
          message:
            "Contact log created successfully",
        });


        setAddContactLogOpen(
          false
        );

        setSelectedUserForLog(
          null
        );


        if (
          contactLogsUser?.user_id
        ) {

          dispatch(
            fetchUserContactLogsAction(
              contactLogsUser.user_id
            )
          );
        }

      } else {

        showToast({
          type: "error",
          message:
            "Failed to create contact log",
          description:
            response?.error ||
            "Unable to create contact log.",
        });
      }
    };


  /* ===================================================== */
  /* VIEW CONTACT LOGS */
  /* ===================================================== */

  const handleViewContactLogs =
    async (
      driver
    ) => {

      const user =
        driver?.user;

      const userId =
        user?.user_id;


      if (!userId) {

        showToast({
          type: "error",
          message:
            "Unable to load contact logs",
          description:
            "User ID is missing for this driver.",
        });

        return;
      }


      setContactLogsUser(
        user
      );

      setContactLogsOpen(
        true
      );


      await dispatch(
        fetchUserContactLogsAction(
          userId
        )
      );
    };


  const handleCloseContactLogs =
    () => {

      setContactLogsOpen(
        false
      );

      setContactLogsUser(
        null
      );
    };


  /* ===================================================== */
  /* MENU */
  /* ===================================================== */

  const generateMenuItems = (
    row
  ) => [

    /* VIEW DRIVER */

    {
      icon:
        <EyeIcon
          size={16}
        />,

      text:
        "View Driver",

      onClick: () =>
        handleViewDriver(
          row.original
        ),
    },


    /* CHAT */

    {
      icon:
        <MessageSquare
          size={16}
        />,

      text:
        "Chat with Driver",

      onClick: () => {

        history.push(
          `/admin/communications?userId=${row.original?.user?.user_id}`
        );

      },
    },


    /* ADD CONTACT LOG */

    {
      icon:
        <FileText
          size={16}
        />,

      text:
        "Add Contact Log",

      onClick: () =>
        handleOpenAddContactLog(
          row.original
        ),
    },


    /* VIEW CONTACT LOGS */

    {
      icon:
        <FileText
          size={16}
        />,

      text:
        "View Contact Logs",

      onClick: () =>
        handleViewContactLogs(
          row.original
        ),
    },


    /* UPDATE DRIVER STATUS */

    {
      icon:
        <Pencil
          size={16}
        />,

      text:
        "Update Driver Status",

      onClick: () =>
        handleOpenEditDriverDrawer(
          row.original
        ),
    },


    /* TOP UP */

    {
      icon:
        <Gift
          size={16}
        />,

      text:
        "Top Up Driver Wallet",

      onClick: () =>
        handleOpenAllocateDrawer(
          row.original
        ),
    },

  ];


  /* ===================================================== */
  /* WALLET LOOKUP */
  /* ===================================================== */

  const walletLookup = {};


  wallets?.forEach(
    (
      wallet
    ) => {

      walletLookup[
        wallet.driver_id
      ] = wallet;

    }
  );


  /* ===================================================== */
  /* COLUMNS */
  /* ===================================================== */

  const columnsData = [

    {
      Header:
        "Driver Name",

      accessor:
        (row) => {

          const firstName =
            row.user
              ?.first_name ||
            "";

          const lastName =
            row.user
              ?.last_name ||
            "";


          return (
            `${firstName} ${lastName}`.trim() ||
            "N/A"
          );
        },
    },


    /* CONTACT STATUS */

    {
      Header:
        "Contact Status",

      accessor:
        (row) =>
          contactStatuses[
            row.user
              ?.user_id
          ] ||
          "Not Contacted",

      Cell:
        ({
          value,
        }) => (

          <StatusBadge
            label={
              value
            }

            variant={
              value ===
              "Not Contacted"
                ? "gray"
                : getStatusVariant(
                    value
                  )
            }
          />

        ),
    },


    {
      Header:
        "License Number",

      accessor:
        (row) =>
          row.driver
            ?.license_number ||
          "N/A",
    },


    {
      Header:
        "License Expiry Date",

      accessor:
        (row) =>
          row.driver
            ?.license_expiry_date,

      Cell:
        ({
          value,
        }) =>
          value
            ? formatDate(
                value
              )
            : "N/A",
    },


    {
      Header:
        "Route Preference",

      accessor:
        (row) =>
          row.driver
            ?.route_preference ||
          "N/A",
    },


    {
      Header:
        "Package Preference",

      accessor:
        (row) =>
          row.driver
            ?.package_preference ||
          "N/A",
    },


    /* DRIVER STATUS */

    {
      Header:
        "Status",

      accessor:
        (row) =>
          row.driver
            ?.status ||
          "Unknown",

      Cell:
        ({
          value,
        }) => (

          <StatusBadge
            label={
              value
            }

            variant={
              getStatusVariant(
                value
              )
            }
          />

        ),
    },


    {
      Header:
        "Created",

      accessor:
        (row) =>
          row.driver
            ?.created_at,

      Cell:
        ({
          value,
        }) =>
          value
            ? formatDate(
                value
              )
            : "N/A",
    },


    /* ACTIONS */

    {
      Header:
        "Actions",

      accessor:
        "moreOptions",

      Cell:
        ({
          row,
        }) => (

          <ThreeDotMenu

            icon={
              <EllipsisVertical
                size={20}
                color="grey"
              />
            }

            items={
              generateMenuItems(
                row
              )
            }

          />

        ),
    },

  ];


  /* ===================================================== */
  /* RENDER */
  /* ===================================================== */

  return (

    <Flex
      flexDirection="column"
      pt={{
        base: "120px",
        md: "25px",
      }}
    >

      {/* HEADER */}

      <Box p={6}>

        <Flex
          justifyContent="space-between"
          alignItems="center"
        >

          <Text
            fontSize="md"
            fontWeight="bold"
          >

            {loading ? (

              <Skeleton
                height="20px"
                width="100px"
              />

            ) : (

              `All Drivers: ${
                total ??
                drivers?.total ??
                0
              }`

            )}

          </Text>

        </Flex>

      </Box>


      {/* TABLE */}

      <ReusableTable

        columnsData={
          columnsData
        }

        tableData={
          Array.isArray(
            drivers?.data
          )
            ? drivers.data
            : []
        }

        isLoading={
          loading
        }

        error={
          error
        }

      />


      {/* ================================================= */}
      {/* PAGINATION - SAME AS USER MANAGEMENT */}
      {/* ================================================= */}

      <Box
        mt={4}
        display="flex"
        justifyContent="center"
        gap={2}
      >

        <GradientButton

          onClick={() =>
            setCurrentPage(
              (prev) =>
                Math.max(
                  prev - 1,
                  1
                )
            )
          }

          disabled={
            currentPage ===
            1
          }

          height="30px"
        >
          Previous
        </GradientButton>


        <Text
          alignSelf="center"
        >
          Page{" "}
          {
            currentPage
          }
        </Text>


        <GradientButton

          onClick={() =>
            setCurrentPage(
              (prev) =>
                prev + 1
            )
          }

          disabled={
            !hasMore
          }

          height="30px"
        >
          Next
        </GradientButton>

      </Box>


      {/* ================================================= */}
      {/* VIEW DRIVER */}
      {/* ================================================= */}

      <ReusableDrawer

        isOpen={
          isViewDriverDrawerOpen
        }

        onClose={
          handleCloseViewDriverDrawer
        }

        headerContent={
          <h2
            style={{
              fontSize:
                "md",
            }}
          >
            View Driver
          </h2>
        }

        bodyContent={

          selectedDriver && (

            <ViewDriver

              driver={
                selectedDriver
              }

              onClose={
                handleCloseViewDriverDrawer
              }

              onTopUp={(
                driver
              ) => {

                handleCloseViewDriverDrawer();

                handleOpenAllocateDrawer(
                  driver
                );

              }}

            />

          )

        }

      />


      {/* ================================================= */}
      {/* EDIT DRIVER */}
      {/* ================================================= */}

      <EditDriverDrawer

        isOpen={
          isEditDriverDrawerOpen
        }

        onClose={
          handleCloseEditDriverDrawer
        }

        driver={
          selectedDriver
        }

      />


      {/* ================================================= */}
      {/* TOP UP DRIVER */}
      {/* ================================================= */}

      <AdminTopUpWalletDrawer

        isOpen={
          isAllocateDrawerOpen
        }

        onClose={
          handleCloseAllocateDrawer
        }

        driver={
          selectedDriver
        }

        onTopUp={
          handleTopUpWallet
        }

      />


      {/* ================================================= */}
      {/* ADD CONTACT LOG */}
      {/* ================================================= */}

      <AddContactLogDrawer

        isOpen={
          isAddContactLogOpen
        }

        onClose={() => {

          setAddContactLogOpen(
            false
          );

          setSelectedUserForLog(
            null
          );

        }}

        onAdd={
          handleAddContactLog
        }

        user={
          selectedUserForLog
        }

      />


      {/* ================================================= */}
      {/* VIEW CONTACT LOGS */}
      {/* ================================================= */}

      <ViewContactLogsDrawer

        isOpen={
          isContactLogsOpen
        }

        onClose={
          handleCloseContactLogs
        }

        logs={
          contactLogs
        }

        user={
          contactLogsUser
        }

        loading={
          contactLogsLoading
        }

      />

    </Flex>
  );
};


export default DriverManagement;